import React from 'react';
import { MonitorIcon, ShieldCheckIcon, TrophyIcon } from 'lucide-react';
import { MatchedSchool } from '../services/matchingApi';
import { normalizeSchool } from '../utils/schoolFields';
interface MatchedSchoolCardProps {
  school: MatchedSchool;
  index: number;
}

/**
 * A live partner-feed listing, built on the same anatomy as the curated guide
 * listing (UniversityListingCard) so a visitor moving between the guides, the
 * home page, and a degree page sees one consistent result card.
 *
 * As with the curated card, the WHOLE card is the link and the orange button is
 * a visual affordance inside it — one tap target, one tab stop, one announced
 * destination per school. Feed copy is partner-supplied, so the fields that may
 * be missing (badge, accreditation, headline, logo) each degrade on their own.
 */
export function MatchedSchoolCard({
  school,
  index
}: MatchedSchoolCardProps) {
  const record = school as Record<string, unknown>;
  const {
    name,
    logo,
    accreditation,
    bodyHtml,
    headline,
    highlight,
    clickUrl
  } = normalizeSchool(record);
  const badge = (highlight || record.ribbonText as string || record.badge as string || '') as string;
  const position = record.mPosition as number || index + 1;
  const isFirst = index === 0;
  const initials = (name || 'SC').split(' ').filter(w => w.length > 0).map(w => w[0]).join('').substring(0, 3).toUpperCase();
  const shellClasses = `block bg-white transition-shadow duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-cta-dark ${isFirst ? 'rounded-b-xl border-2 border-t-0 border-navy/20 shadow-lg' : 'rounded-xl border border-gray-200 shadow-sm'} ${clickUrl ? 'group hover:shadow-lg' : ''}`;
  const logoMark = <>
      {logo ? <img src={logo} alt="" aria-hidden="true" className="max-w-full max-h-full object-contain" loading="lazy" onError={e => {
      const img = e.target as HTMLImageElement;
      img.style.display = 'none';
      const fallback = img.nextElementSibling as HTMLElement | null;
      if (fallback) fallback.style.display = 'flex';
    }} data-id="element-780" data-mp="10igycfplh4we" /> : null}
      <span className={`w-full h-full items-center justify-center ${logo ? 'hidden' : 'flex'}`} data-id="element-781" data-mp="2cmg4wd29xrlf">
        <span className="text-base font-bold text-navy" data-id="element-782" data-mp="1ovu0cvrhapg6">{initials}</span>
      </span>
    </>;
  const badgePill = badge ? <span className="inline-flex items-center justify-center text-center bg-light border border-gray-200 text-navy text-[11px] sm:text-xs font-semibold px-3 py-1.5 rounded-full" data-id="element-783" data-mp="1jyu2370veqzm">
      {badge}
    </span> : null;
  const content = <div className="p-4 sm:p-6 lg:p-7 flex flex-col lg:flex-row lg:gap-8" data-id="element-784" data-mp="1ty2v9xnww1kx">
      {/* Main content */}
      <div className="flex-1 min-w-0" data-id="element-785" data-mp="35avl32x56fyc">
        {/* Narrow-screen header: rank and badge on one line, then a large
            centred logo. On desktop the rank moves inline beside the name and
            the logo moves to the right column. */}
        <div className="lg:hidden" data-id="element-786" data-mp="3huipndm5hu40">
          <div className="flex items-center justify-between gap-3" data-id="element-787" data-mp="3hrehk55kee82">
            <span className={`inline-flex items-center justify-center rounded-full px-3 py-1 text-xs font-bold ${isFirst ? 'bg-navy text-white' : 'border border-gray-200 text-gray-500'}`} data-id="element-788" data-mp="32xi9869etlku">
              #{position}
              <span className="sr-only" data-id="element-789" data-mp="4e1ocuu6oy16"> ranked</span>
            </span>
            {badgePill}
          </div>

          <div className="mt-5 mb-6 h-16 sm:h-20 flex items-center justify-center" data-id="element-790" data-mp="3lt0129cltqr8">
            {logoMark}
          </div>
        </div>

        <div className="flex gap-3 sm:gap-4" data-id="element-791" data-mp="iem2vet2g7ov">
          <div className="hidden lg:block flex-shrink-0 pt-0.5" data-id="element-792" data-mp="icszq9hnw5lm">
            <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${isFirst ? 'bg-navy text-white' : 'border-2 border-gray-200 text-gray-500'}`} data-id="element-793" data-mp="n5ca6xl1ykm7">
              <span className="text-sm font-bold" data-id="element-794" data-mp="2lnz51izqjbkg">{position}</span>
              <span className="sr-only" data-id="element-795" data-mp="2lo7ltlyzb10x">Ranked number {position}</span>
            </div>
          </div>

          <div className="flex-1 min-w-0" data-id="element-796" data-mp="2gqz2vj60asym">
            <h3 className="text-xl sm:text-2xl font-bold text-navy leading-tight text-center lg:text-left" data-id="element-797" data-mp="3ed64qlaxe77a">
              {name}
            </h3>

            <div className="mt-1.5 sm:mt-2 flex flex-wrap items-center justify-center lg:justify-start gap-x-3 sm:gap-x-4 gap-y-1 text-xs text-gray-500" data-id="element-798" data-mp="39ebnhnslwqpa">
              {accreditation && <span className="inline-flex items-center gap-1.5" data-id="element-799" data-mp="2zn78wp6uf7uz">
                  <ShieldCheckIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-800" data-mp="y01dlpq3onn" />
                  {accreditation}
                </span>}
              {/* On narrow screens this sits under the button instead, where it
                  reassures at the moment of the tap. */}
              <span className="hidden lg:inline-flex items-center gap-1.5" data-id="element-801" data-mp="dglwtxha0qe8">
                <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-802" data-mp="diozbgzm8bzu" />
                100% Online
              </span>
            </div>

            {/* The reason to click. Desktop only — on a phone the bullets carry
                the pitch and this line only pushes the button down. */}
            {headline && <p className="hidden lg:block mt-4 text-lg font-bold leading-snug" style={{
            color: '#332B66'
          }} data-id="element-803" data-mp="w96fkm39hf4b">
                {headline}
              </p>}

            {/* Partner-supplied selling points, already marked up as a list. */}
            {bodyHtml && <div className="school-body-content mt-4 sm:mt-5 text-left" dangerouslySetInnerHTML={{
            __html: bodyHtml
          }} data-id="element-804" data-mp="lffk7sgfbe6s" />}
          </div>
        </div>
      </div>

      {/* Call to action. A styled span, not a link — the card carries the
          destination. */}
      <div className="mt-4 pt-4 border-t border-gray-100 sm:mt-6 sm:pt-6 lg:mt-0 lg:pt-0 lg:border-t-0 lg:border-l lg:pl-8 lg:w-60 lg:flex-shrink-0 flex flex-col items-center justify-center gap-3 sm:gap-4" data-id="element-805" data-mp="qea12outyso9">
        <span className="hidden lg:inline-flex" data-id="element-806" data-mp="z6u3m0e8thed">{badgePill}</span>

        <span className="hidden lg:flex w-full h-28 items-center justify-center" data-id="element-807" data-mp="1e55wrx2eq80s">
          {logoMark}
        </span>

        {clickUrl && <span className="flex items-center justify-center text-center w-full min-h-[52px] sm:min-h-[60px] px-6 py-3.5 sm:py-4 text-base font-bold rounded-xl text-white bg-cta shadow-md group-hover:bg-cta-dark group-hover:shadow-lg transition-[background-color,box-shadow] duration-200 ease-out" data-id="element-808" data-mp="196b5s7uadd5l">
            View Programs
          </span>}

        <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-600" data-id="element-809" data-mp="1veglntp7agt5">
          <MonitorIcon className="w-3.5 h-3.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-810" data-mp="1gpc0q4r0tyhc" />
          <span className="lg:hidden" data-id="element-811" data-mp="1ln3wxayp86rc">100% Online</span>
          <span className="hidden lg:inline" data-id="element-812" data-mp="17odxvpsbjx0c">Online Degree Options</span>
        </span>
      </div>
    </div>;
  return <article className="relative" data-id="element-813" data-mp="1bq7vfp77wkwa">
      {isFirst && <div className="rounded-t-xl px-4 py-2 sm:py-3 flex items-center justify-center gap-2" style={{
      backgroundColor: '#5FA396'
    }} data-id="element-814" data-mp="1bnv6gs31la9q">
          <TrophyIcon className="w-4 h-4 text-gold-light" aria-hidden="true" data-id="element-815" data-mp="12q2p7idrfsjp" />
          <span className="text-white text-sm font-bold tracking-wide" data-id="element-816" data-mp="2s3y293pq1n9m">
            #1 Recommended School
          </span>
        </div>}

      {clickUrl ? <a href={clickUrl} target="_blank" rel="noopener noreferrer" className={shellClasses} aria-label={`View programs at ${name} (opens in a new tab)`} data-id="element-817" data-mp="1jwg21bumd8ev">
          {content}
        </a> : <div className={shellClasses} data-id="element-818" data-mp="1b2kvq0hrhbxn">{content}</div>}
    </article>;
}
export function DidYouKnowBanner() {
  return <div className="flex items-center justify-center gap-2 py-3 text-sm text-gray-600" data-id="element-819" data-mp="1638afk6jgcx6">
      <span className="text-lg" aria-hidden="true" data-id="element-820" data-mp="113vca3a7vjfa">
        💡
      </span>
      <span className="font-bold text-gray-800" data-id="element-821" data-mp="w63it4txbp8z">DID YOU KNOW?</span>
      <span data-id="element-822" data-mp="s4rt6zb1v40s">Enrollments increased by 40% this year</span>
    </div>;
}